import React, { useCallback, useEffect, useState } from "react";
import { TopBar } from "../components/TopBar";
import { MetricsBar } from "../components/MetricsBar";
import { LedgerColumn } from "../components/LedgerColumn";
import { DetailDialog } from "../components/DetailDialog";
import { NeedsAttention, ChangedRecently } from "../components/HomeOperations";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CalendarPlus, RefreshCw, AlertTriangle } from "lucide-react";

const GROUPS = [
  { key: "all", label: "All" },
  { key: "due_today", label: "Due Today" },
  { key: "due_soon", label: "Due Soon" },
  { key: "overdue", label: "Overdue" },
  { key: "conditional", label: "Conditional" },
  { key: "blocked", label: "Blocked" },
  { key: "partially_fulfilled", label: "Partial" },
  { key: "fulfilled", label: "Fulfilled" },
  { key: "cancelled", label: "Cancelled" },
  { key: "dismissed", label: "Dismissed" },
];

export default function Dashboard({ setPasteOpen, refreshTick, commitmentsOnly = false }) {
  const [metrics, setMetrics] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [attention, setAttention] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [group, setGroup] = useState("all");
  const [batch, setBatch] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(false);

  const refresh = useCallback(async () => {
    const [m, c, a, impact] = await Promise.all([
      api.getMetrics(),
      api.listCommitments(),
      api.getNeedsAttention(),
      api.getImpact(),
    ]);
    setMetrics(m);
    setCommitments(c);
    setAttention(a);
    setRecentEvents(impact.recent_events || []);
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const s = await api.demoStatus().catch(() => ({ seeded: false, ok: false }));
      if (!s.seeded) {
        try {
          await api.seed();
        } catch {
          setBootstrapError(true);
          setInitialLoading(false);
          return;
        }
      }
      setBootstrapError(false);
      await refresh();
    } catch {
      setBootstrapError(true);
    } finally {
      setInitialLoading(false);
    }
  }, [refresh]);

  const restoreDemo = async () => {
    setInitialLoading(true);
    try {
      await api.seed();
      setBootstrapError(false);
      await refresh();
      toast.success("Demo data restored");
    } catch {
      toast.error("Could not restore demo data — try again in a moment");
      setBootstrapError(true);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { if (refreshTick) refresh(); }, [refreshTick, refresh]);

  useEffect(() => {
    const handler = (e) => {
      const accepted = e.detail || [];
      if (!accepted.length) return;
      refresh();
      const byMeWithDates = accepted.filter((c) => c.direction === "by_me" && c.expected_completion);
      setBatch({ items: accepted, byMeWithDates });
    };
    const ffHandler = () => refresh();
    window.addEventListener("cl:accepted", handler);
    window.addEventListener("cl:fulfilled", ffHandler);
    return () => {
      window.removeEventListener("cl:accepted", handler);
      window.removeEventListener("cl:fulfilled", ffHandler);
    };
  }, [refresh]);

  const openDetail = (c) => { setDetailId(c.id); setDetailOpen(true); };

  const batchAddCalendar = async () => {
    if (!batch?.byMeWithDates?.length) return;
    const ids = batch.byMeWithDates.map((c) => c.id);
    try {
      const res = await fetch(api.icsBatchUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "commitments.ics"; a.click();
      URL.revokeObjectURL(url);
      await Promise.all(
        batch.byMeWithDates.map((c) =>
          api.addCalendar(c.id, { title: c.promise_text, date: c.expected_completion, note: "Demo calendar event (batch)" })
        )
      );
      toast.success("Demo calendar events saved · .ics downloaded");
      setBatch(null);
      refresh();
    } catch {
      toast.error("Batch calendar failed");
    }
  };

  const doReset = async () => {
    try {
      await api.reset();
      toast.success("Demo data reset");
      setResetConfirm(false);
      await refresh();
    } catch {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="min-h-screen paper-grain" data-testid="dashboard-page">
      <TopBar onOpenPaste={() => setPasteOpen(true)} />

      <main className="mx-auto max-w-[1700px] px-6 md:px-12 py-10 md:py-14">
        <div className="mb-10 md:mb-12 animate-fadeUp flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-4xl">
            <div
              className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted"
              data-testid="hero-eyebrow"
            >
              Northstar Creative · Client operations
            </div>
            <h1
              className="mt-2 font-display text-4xl md:text-5xl text-ledger-ink"
              data-testid="page-title"
            >
              {commitmentsOnly
                ? "Every commitment, with its current status and evidence."
                : "Know what your clients promised your team, what your team promised back, and what changed afterward."}
            </h1>
            <p
              className="mt-3 text-ledger-sub text-base max-w-3xl"
              data-testid="hero-description"
            >
              {commitmentsOnly
                ? "Review the living record across client relationships, including revisions, fulfilment, and the original conversation evidence."
                : "Commitment Ledger turns important conversations into a living, evidence-backed record, then keeps that record current as promises change or are fulfilled."}
            </p>
          </div>
          <button
            onClick={() => setResetConfirm(true)}
            data-testid="reset-demo"
            className="inline-flex w-fit items-center gap-2 px-3 py-2 rounded-full border border-ledger-border bg-white font-mono text-[11px] uppercase tracking-wider text-ledger-muted hover:border-ledger-ink hover:text-ledger-ink"
          >
            <RefreshCw className="h-3 w-3" /> Reset sample data
          </button>
        </div>

        {bootstrapError && (
          <div
            data-testid="bootstrap-error"
            className="mb-8 bg-[#FEE2E2] border border-[#EF4444] rounded-sm p-4 flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 text-[#B91C1C] mt-0.5" />
            <div className="flex-1">
              <div className="font-sans text-sm text-[#7F1D1D] font-semibold">
                Couldn&rsquo;t load the demo dataset
              </div>
              <div className="text-[12px] text-[#7F1D1D] mt-0.5">
                The ledger is empty. Click Restore to seed the canonical demo commitments.
              </div>
            </div>
            <button
              onClick={restoreDemo}
              data-testid="restore-demo"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ledger-ink text-white text-xs font-medium hover:bg-neutral-800"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Restore demo data
            </button>
          </div>
        )}

        {!commitmentsOnly && (
          <>
            <div className="animate-fadeUp" style={{ animationDelay: "60ms" }}>
              <NeedsAttention data={attention} loading={initialLoading} onOpen={openDetail} />
            </div>

            <div className="animate-fadeUp" style={{ animationDelay: "90ms" }}>
              <ChangedRecently events={recentEvents} loading={initialLoading} onOpen={openDetail} />
            </div>
          </>
        )}

        <section className="mt-14 animate-fadeUp" style={{ animationDelay: "110ms" }} data-testid="home-summary">
          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">At a glance</div>
            <h2 className="mt-1 font-display text-2xl text-ledger-ink">Commitment summary</h2>
          </div>
          {initialLoading ? (
            <div className="h-40 bg-ledger-surface border border-ledger-border grid place-items-center font-mono text-sm text-ledger-muted">
              Loading summary…
            </div>
          ) : <MetricsBar metrics={metrics} />}
        </section>

        <div id="commitments" className="mt-14 border-t border-ledger-border pt-10">
          <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">Full ledger</div>
            <h2 className="mt-1 font-display text-3xl text-ledger-ink">All commitments</h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 animate-fadeUp" style={{ animationDelay: "120ms" }}>
          <span className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted mr-2">View</span>
          {GROUPS.map((g) => (
            <button
              key={g.key}
              data-testid={`group-${g.key.replace(/_/g, "-")}`}
              onClick={() => setGroup(g.key)}
              className={
                "px-3 py-1 font-mono text-[11px] uppercase tracking-wider rounded-sm border transition-colors duration-150 " +
                (group === g.key
                  ? "bg-ledger-ink text-white border-ledger-ink"
                  : "bg-white text-ledger-sub border-ledger-border hover:border-ledger-ink")
              }
            >
              {g.label}
            </button>
          ))}
        </div>

        <div
          className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 animate-fadeUp"
          style={{ animationDelay: "120ms" }}
        >
          <LedgerColumn
            direction="to_me"
            title="Promised to us"
            subtitle="What clients owe Northstar"
            commitments={commitments}
            onOpen={openDetail}
            sharedFilter={group === "all" ? null : group}
          />
          <LedgerColumn
            direction="by_me"
            title="Promised by us"
            subtitle="What Northstar owes clients"
            commitments={commitments}
            onOpen={openDetail}
            sharedFilter={group === "all" ? null : group}
          />
        </div>

        <footer className="mt-24 pt-8 border-t border-ledger-border text-center">
          <div className="font-display text-lg text-ledger-ink">
            Conversations are not systems of record.
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-ledger-muted">
            Commitment Ledger is.
          </div>
        </footer>
      </main>

      {batch && batch.byMeWithDates.length > 0 && (
        <div
          data-testid="batch-toast"
          className="fixed bottom-6 right-6 z-50 max-w-md bg-white border border-ledger-border shadow-lg rounded-sm p-4 flex items-start gap-3 animate-fadeUp"
        >
          <div className="h-8 w-8 rounded-sm bg-ledger-goldBg border border-ledger-goldBorder grid place-items-center shrink-0">
            <CalendarPlus className="h-4 w-4 text-[#B45309]" />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-ledger-ink">
              {batch.items.length} commitment{batch.items.length === 1 ? "" : "s"} added
            </div>
            <div className="text-ledger-sub text-[13px] mt-0.5">
              {batch.byMeWithDates.length} promise{batch.byMeWithDates.length === 1 ? "" : "s"} you made {batch.byMeWithDates.length === 1 ? "has" : "have"} deadlines. Add {batch.byMeWithDates.length === 1 ? "it" : "them"} as demo calendar events?
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={batchAddCalendar}
                data-testid="batch-calendar-yes"
                className="px-3 py-1.5 rounded-full bg-ledger-ink text-white text-xs font-medium hover:bg-neutral-800"
              >
                Download .ics
              </button>
              <button
                onClick={() => setBatch(null)}
                data-testid="batch-calendar-no"
                className="px-3 py-1.5 rounded-full border border-ledger-border text-xs text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {resetConfirm && (
        <div
          data-testid="reset-confirm"
          className="fixed inset-0 z-50 grid place-items-center bg-black/30"
          onClick={() => setResetConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-ledger-border rounded-sm shadow-lg max-w-md p-6"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[#B45309]" />
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ledger-muted">
                Reset demo data
              </div>
            </div>
            <div className="mt-2 font-display text-2xl text-ledger-ink">
              This deletes everything and restores the seeded scenario.
            </div>
            <div className="mt-2 text-sm text-ledger-sub">
              Overdue invoice, conditional MSA, financial promises, and one fulfilled example will be re-created. Any custom edits will be lost.
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setResetConfirm(false)}
                data-testid="reset-cancel"
                className="px-4 py-2 text-sm text-ledger-sub hover:text-ledger-ink"
              >
                Cancel
              </button>
              <button
                onClick={doReset}
                data-testid="reset-confirm-yes"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-ledger-ink text-white text-sm font-medium hover:bg-neutral-800"
              >
                <RefreshCw className="h-4 w-4" /> Reset
              </button>
            </div>
          </div>
        </div>
      )}

      <DetailDialog
        commitmentId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onChanged={refresh}
      />
    </div>
  );
}
